/// <reference types="vite/client" />
import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";

// ─── Wire format ──────────────────────────────────────────────────────
// Draft review lives on /posts (Posts Queue, #20). This page is now the
// account-configuration surface only: brand voice, topic mix, frequency,
// auto-publish, and per-account metrics.

interface BrandVoice {
  tone: string;
  pronouns: string;
  dos: string[];
  donts: string[];
}

interface TopicMix {
  topics: Array<{ name: string; weight: number; sample_angles?: string[] }>;
}

interface SocialAccountRow {
  id: string;
  display_name: string;
  type: string;
  unipile_account_id_set: boolean;
  brand_voice: BrandVoice;
  topic_mix: TopicMix;
  frequency_per_week: number;
  auto_publish: boolean;
  active: boolean;
}

interface AccountMetricsRoll {
  id: string;
  display_name: string;
  posts_this_week: number;
  posts_last_week: number;
  total_likes: number;
  total_comments: number;
  total_reach: number;
}

interface EngagementRow {
  id: string;
  name: string;
  kind: string;
  status: string;
}

const FIELD_STYLE = { background: "var(--color-bg-card)", border: "1px solid var(--color-border)", color: "var(--color-text)" };
const ACCENT_BTN = { background: "var(--color-accent-soft)", color: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" };

export function LinkedinHub() {
  const [accounts, setAccounts] = useState<SocialAccountRow[]>([]);
  const [metrics, setMetrics] = useState<AccountMetricsRoll[]>([]);
  const [engagements, setEngagements] = useState<EngagementRow[]>([]);
  const [engagement, setEngagement] = useState<string>("levier-ia-internal");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"page" | "profile">("page");
  const [addBusy, setAddBusy] = useState(false);

  // Tenant list (engagement selector) — loaded once.
  useEffect(() => {
    fetch("/api/engagements/list")
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setEngagements(Array.isArray(rows) ? (rows as EngagementRow[]) : []))
      .catch(() => undefined);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountsRes, metricsRes] = await Promise.all([
        fetch(`/api/social-accounts?engagement=${encodeURIComponent(engagement)}`),
        fetch("/api/social-accounts/metrics"),
      ]);
      if (!accountsRes.ok) throw new Error(`accounts HTTP ${accountsRes.status}`);
      const { accounts: a } = (await accountsRes.json()) as { accounts: SocialAccountRow[] };
      setAccounts(a ?? []);
      if (metricsRes.ok) {
        const { accounts: m } = (await metricsRes.json()) as { accounts: AccountMetricsRoll[] };
        setMetrics(m ?? []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [engagement]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const patchAccount = useCallback(async (id: string, body: Record<string, unknown>) => {
    try {
      const res = await fetch(`/api/social-accounts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [loadAll]);

  // Open the Unipile hosted-auth flow for an account; the notify webhook
  // binds the returned account_id back to the row.
  const connectAccount = useCallback(async (id: string) => {
    setError(null);
    try {
      const res = await fetch(`/api/social-accounts/${id}/connect`, { method: "POST" });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(e.message ?? `HTTP ${res.status}`);
      }
      const { url } = (await res.json()) as { url: string };
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const submitNewAccount = useCallback(async () => {
    if (!newName.trim()) return;
    setAddBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/social-accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: newName.trim(), type: newType, channel: "linkedin", engagement_id: engagement }),
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(e.message ?? `HTTP ${res.status}`);
      }
      setNewName("");
      setShowAdd(false);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddBusy(false);
    }
  }, [newName, newType, engagement, loadAll]);

  if (error && accounts.length === 0 && !showAdd) {
    return <ErrorBanner message={error} onRetry={loadAll} context="linkedin hub" />;
  }

  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="reveal">
        <p className="eyebrow">Console · 11</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">LinkedIn </span>
          <span className="text-accent-gradient">Hub</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Configuration des comptes sociaux : voix de marque, topic mix, fréquence, auto-publish. La file des drafts est sur Posts.
        </p>
      </header>

      <section className="space-y-4">
        <div className="section-rule flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow">// Comptes sociaux</p>
          <div className="flex items-center gap-2">
            <select
              value={engagement}
              onChange={(e) => setEngagement(e.target.value)}
              className="font-mono text-[11px] px-2 py-1 rounded"
              style={FIELD_STYLE}
              aria-label="Engagement"
            >
              {engagements.length === 0 && <option value="levier-ia-internal">levier-ia-internal</option>}
              {engagements.map((e) => (
                <option key={e.id} value={e.id}>{e.name} ({e.kind})</option>
              ))}
            </select>
            <button
              onClick={() => setShowAdd((s) => !s)}
              className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded transition-colors"
              style={ACCENT_BTN}
            >
              {showAdd ? "Annuler" : "+ Ajouter un compte"}
            </button>
          </div>
        </div>

        {showAdd && (
          <div className="panel p-4 space-y-3">
            <p className="font-mono text-[11px] text-[var(--color-text-dim)]">
              Nouveau compte sur l'engagement <span className="text-[var(--color-text)]">{engagement}</span>. Après création, cliquez « Connecter via Unipile » sur la carte.
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nom affiché (ex. Levier IA — Page)"
                className="flex-1 min-w-[200px] font-mono text-[12px] px-2 py-1.5 rounded"
                style={FIELD_STYLE}
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as "page" | "profile")}
                className="font-mono text-[12px] px-2 py-1.5 rounded"
                style={FIELD_STYLE}
                aria-label="Type de compte"
              >
                <option value="page">page</option>
                <option value="profile">profile</option>
              </select>
              <button
                onClick={submitNewAccount}
                disabled={addBusy || !newName.trim()}
                className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors disabled:opacity-40"
                style={ACCENT_BTN}
              >
                {addBusy ? "…" : "Créer"}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <AccountCard
              key={a.id}
              account={a}
              metrics={metrics.find((m) => m.id === a.id)}
              onPatch={(body) => patchAccount(a.id, body)}
              onConnect={() => connectAccount(a.id)}
            />
          ))}
          {accounts.length === 0 && !loading && (
            <p className="font-mono text-xs text-[var(--color-text-dim)]">Aucun compte social configuré pour cet engagement.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function AccountCard({
  account,
  metrics,
  onPatch,
  onConnect,
}: {
  account: SocialAccountRow;
  metrics?: AccountMetricsRoll;
  onPatch: (body: Record<string, unknown>) => void;
  onConnect: () => void;
}) {
  const [frequency, setFrequency] = useState(account.frequency_per_week);
  const [editingVoice, setEditingVoice] = useState(false);
  const [voiceDraft, setVoiceDraft] = useState<BrandVoice>(account.brand_voice);
  const [editingTopics, setEditingTopics] = useState(false);
  const [topicsDraft, setTopicsDraft] = useState<TopicMix>(account.topic_mix);
  const [unipileDraft, setUnipileDraft] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refresh local state if the parent re-loads the accounts.
  useEffect(() => {
    setVoiceDraft(account.brand_voice);
    setTopicsDraft(account.topic_mix);
    setFrequency(account.frequency_per_week);
    setUnipileDraft("");
  }, [account]);

  const saveVoice = async () => {
    setSaveError(null);
    if (!voiceDraft.tone || !voiceDraft.pronouns) {
      setSaveError("`tone` et `pronouns` requis");
      return;
    }
    await onPatch({ brand_voice: voiceDraft });
    setEditingVoice(false);
  };

  const saveTopics = async () => {
    setSaveError(null);
    if (!Array.isArray(topicsDraft.topics) || topicsDraft.topics.length === 0) {
      setSaveError("au moins 1 topic requis");
      return;
    }
    await onPatch({ topic_mix: topicsDraft });
    setEditingTopics(false);
  };

  return (
    <div className="panel p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <h4 className="font-display italic text-xl text-[var(--color-text)]">{account.display_name}</h4>
        <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
          {account.type.toUpperCase()}
        </span>
      </div>

      {!account.unipile_account_id_set && (
        <div className="mt-2 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--color-status-error)" }}>
            ⚠ Unipile auth manquant
          </p>
          <button
            onClick={onConnect}
            className="w-full font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
            style={{ background: "var(--color-accent-soft)", color: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}
          >
            Connecter via Unipile
          </button>
          <details>
            <summary className="font-mono text-[10px] cursor-pointer" style={{ color: "var(--color-text-dim)" }}>
              Saisir l'account_id manuellement
            </summary>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={unipileDraft}
                onChange={(e) => setUnipileDraft(e.target.value)}
                placeholder="account_id Unipile"
                className="flex-1 min-w-0 font-mono text-[11px] px-2 py-1 rounded"
                style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
              />
              <button
                onClick={() => {
                  const v = unipileDraft.trim();
                  if (v) onPatch({ unipile_account_id: v });
                }}
                disabled={!unipileDraft.trim()}
                className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded transition-colors disabled:opacity-40"
                style={{ background: "var(--color-bg-card)", color: "var(--color-text-muted)", border: "1px solid var(--color-border)" }}
              >
                Lier
              </button>
            </div>
          </details>
        </div>
      )}

      {metrics && (
        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
          <MetricMini label="Posts 7j" value={metrics.posts_this_week} previous={metrics.posts_last_week} />
          <MetricMini label="Likes" value={metrics.total_likes} />
          <MetricMini label="Comments" value={metrics.total_comments} />
          <MetricMini label="Reach" value={metrics.total_reach} compact />
        </div>
      )}

      <div className="mt-4 space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] text-[var(--color-text-dim)]">Auto-publish</span>
          <button
            onClick={() => onPatch({ auto_publish: !account.auto_publish })}
            className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded transition-colors"
            style={{
              background: account.auto_publish ? "var(--color-accent-soft)" : "var(--color-bg-card)",
              color: account.auto_publish ? "var(--color-accent-glow)" : "var(--color-text-muted)",
              border: `1px solid ${account.auto_publish ? "var(--color-accent)" : "var(--color-border)"}`,
            }}
          >
            {account.auto_publish ? "Auto" : "Draft + review"}
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] text-[var(--color-text-dim)]">Fréquence/semaine</span>
            <span className="font-mono text-xs text-[var(--color-text)]">{frequency}</span>
          </div>
          <input
            type="range"
            min={1}
            max={7}
            value={frequency}
            onChange={(e) => setFrequency(Number(e.target.value))}
            onMouseUp={() => onPatch({ frequency_per_week: frequency })}
            onTouchEnd={() => onPatch({ frequency_per_week: frequency })}
            className="w-full mt-1"
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] text-[var(--color-text-dim)]">Brand voice</p>
            <button
              onClick={() => setEditingVoice((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] hover:text-[var(--color-accent-bright)]"
            >
              {editingVoice ? "Annuler" : "Éditer"}
            </button>
          </div>

          {editingVoice ? (
            <div className="mt-2 space-y-2 text-[12px]">
              <LabeledInput label="Tone" value={voiceDraft.tone} onChange={(tone) => setVoiceDraft({ ...voiceDraft, tone })} />
              <LabeledInput label="Pronouns" value={voiceDraft.pronouns} onChange={(pronouns) => setVoiceDraft({ ...voiceDraft, pronouns })} />
              <LabeledArea
                label="À faire (1 par ligne)"
                rows={3}
                value={voiceDraft.dos.join("\n")}
                onChange={(t) => setVoiceDraft({ ...voiceDraft, dos: t.split("\n").map((s) => s.trim()).filter(Boolean) })}
              />
              <LabeledArea
                label="À éviter (1 par ligne)"
                rows={3}
                value={voiceDraft.donts.join("\n")}
                onChange={(t) => setVoiceDraft({ ...voiceDraft, donts: t.split("\n").map((s) => s.trim()).filter(Boolean) })}
              />
              <div className="flex justify-end pt-1">
                <button
                  onClick={saveVoice}
                  className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded"
                  style={{ background: "var(--color-accent-soft)", color: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-1 space-y-0.5">
              <p className="font-mono text-[11px] text-[var(--color-text-muted)] truncate">tone: {account.brand_voice.tone}</p>
              <p className="font-mono text-[11px] text-[var(--color-text-muted)] truncate">pronouns: {account.brand_voice.pronouns}</p>
              <p className="font-mono text-[10px] text-[var(--color-text-dim)]">{account.brand_voice.dos.length} dos · {account.brand_voice.donts.length} donts</p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] text-[var(--color-text-dim)]">Topic mix</p>
            <button
              onClick={() => setEditingTopics((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] hover:text-[var(--color-accent-bright)]"
            >
              {editingTopics ? "Annuler" : "Éditer"}
            </button>
          </div>
          {editingTopics ? (
            <div className="mt-2 space-y-2 text-[12px]">
              {topicsDraft.topics.map((t, idx) => (
                <div key={idx} className="p-2 rounded space-y-1" style={{ border: "1px solid var(--color-border)", background: "var(--color-bg-card)" }}>
                  <LabeledInput
                    label="Nom"
                    value={t.name}
                    onChange={(name) => {
                      const next = [...topicsDraft.topics];
                      next[idx] = { ...next[idx], name };
                      setTopicsDraft({ topics: next });
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[var(--color-text-dim)] w-16">Weight</span>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={t.weight}
                      onChange={(e) => {
                        const next = [...topicsDraft.topics];
                        next[idx] = { ...next[idx], weight: Number(e.target.value) };
                        setTopicsDraft({ topics: next });
                      }}
                      className="font-mono text-[11px] px-2 py-1 rounded w-16"
                      style={{ background: "var(--color-bg)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
                    />
                    <button
                      onClick={() => setTopicsDraft({ topics: topicsDraft.topics.filter((_, i) => i !== idx) })}
                      className="ml-auto font-mono text-[10px] text-[var(--color-status-error)] hover:underline"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => setTopicsDraft({ topics: [...topicsDraft.topics, { name: "", weight: 1, sample_angles: [] }] })}
                className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] hover:text-[var(--color-accent-bright)]"
              >
                + Ajouter un topic
              </button>
              <div className="flex justify-end pt-1">
                <button
                  onClick={saveTopics}
                  className="font-mono text-[10px] uppercase tracking-wider px-3 py-1 rounded"
                  style={{ background: "var(--color-accent-soft)", color: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <ul className="mt-1 space-y-0.5">
              {account.topic_mix.topics.map((t, idx) => (
                <li key={idx} className="font-mono text-[11px] text-[var(--color-text-muted)] truncate">
                  <span className="text-[var(--color-text-dim)]">[{t.weight}]</span> {t.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {saveError && (
          <p className="font-mono text-[10px]" style={{ color: "var(--color-status-error)" }}>
            ⚠ {saveError}
          </p>
        )}
      </div>
    </div>
  );
}

function MetricMini({
  label,
  value,
  previous,
  compact,
}: {
  label: string;
  value: number;
  previous?: number;
  compact?: boolean;
}) {
  const fmtValue = compact && value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value);
  let arrow = "";
  let arrowColor = "var(--color-text-dim)";
  if (previous !== undefined) {
    if (value > previous) {
      arrow = "↑";
      arrowColor = "var(--color-status-active)";
    } else if (value < previous) {
      arrow = "↓";
      arrowColor = "var(--color-status-error)";
    } else {
      arrow = "→";
    }
  }
  return (
    <div className="px-2 py-1.5 rounded" style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}>
      <p className="font-mono text-[9px] uppercase tracking-wider text-[var(--color-text-dim)]">{label}</p>
      <p className="font-mono text-sm font-medium text-[var(--color-text)] tabular-nums">
        {fmtValue} {arrow && <span style={{ color: arrowColor }}>{arrow}</span>}
      </p>
    </div>
  );
}

function LabeledInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] block mb-0.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-sans text-sm px-2 py-1 rounded"
        style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
      />
    </div>
  );
}

function LabeledArea({ label, value, onChange, rows }: { label: string; value: string; onChange: (v: string) => void; rows: number }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] block mb-0.5">{label}</label>
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-sans text-sm px-2 py-1 rounded resize-y"
        style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
      />
    </div>
  );
}
